import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * 페이지당 개수 선택지 (AMZUWEB-684).
 *
 * 어드민 공용 `PAGE_SIZE_OPTIONS`(`constants/service/common/utilConstants`)와 같은 값이다 —
 * 리스트마다 선택지가 다르면 같은 일을 하는 드롭다운이 화면마다 다른 숫자를 보여준다.
 */
export const LIST_PAGE_SIZE_OPTIONS = [20, 50, 100, 500] as const;

export const DEFAULT_LIST_PAGE_SIZE = 20;

export type ListSortOrder = 'ASC' | 'DESC';

/** 필터 + 페이지 + 정렬 — 화면이 서버에 보내는 것 전부 */
export type ListQuery<TFilter> = TFilter & {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: ListSortOrder;
};

type ListStateConfig<TFilter> = {
  /** 쿼리가 비었거나 깨졌을 때 돌아갈 자리 */
  defaultFilter: TFilter;
  /** URL 쿼리 → 도메인 필터. **던지지 말 것** — 못 읽는 값은 기본값으로 떨어뜨린다 */
  parse: (params: URLSearchParams) => TFilter;
  /** 도메인 필터 → URL 쿼리. 기본값과 같은 항목은 넣지 않아 URL 을 짧게 유지한다 */
  serialize: (filter: TFilter) => Record<string, string>;
  /** 정렬 허용 필드 — 이 밖의 값은 무시한다 (서버가 모르는 컬럼으로 정렬하면 500 이 난다) */
  sortableFields?: readonly string[];
  defaultSortBy?: string;
  defaultSortOrder?: ListSortOrder;
  defaultPageSize?: number;
};

export type ListState<TFilter> = {
  /** 편집 중인 필터 — 검색 버튼을 눌러야 `query` 에 반영된다 */
  draft: TFilter;
  setDraft: (filter: TFilter) => void;
  /** 서버에 보낼 것. URL 이 정본이다 */
  query: ListQuery<TFilter>;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: ListSortOrder;
  /** draft 를 확정해 URL 에 싣는다 (페이지는 1 로 되돌린다) */
  search: () => void;
  /** 필터·페이지·정렬을 전부 기본값으로 */
  reset: () => void;
  setPage: (page: number, pageSize?: number) => void;
  setSort: (sortBy?: string, sortOrder?: ListSortOrder) => void;
  /** 응답의 총건수를 알려주면 범위를 넘은 페이지를 마지막 페이지로 되돌린다 */
  clampPage: (totalCount: number) => void;
};

const readPositiveInt = (raw: string | null, fallback: number): number => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * 리스트 화면 공통 상태 (AMZUWEB-684) — **URL 쿼리가 정본이다.**
 *
 * amondzlab 은 같은 문제를 `useListTableState`(localStorage)로 풀었는데 admin 은 URL 로 간다.
 * 출발점이 *"필터 걸린 리스트 링크를 슬랙으로 주고받고 싶다"* 였고, 그러려면 상태가 주소에 있어야 한다.
 * 새로고침·뒤로가기가 공짜로 따라오는 것도 localStorage 가 못 주는 이점이다.
 *
 * ⚠️ **대가 — 사용자가 주소창을 직접 고칠 수 있다.** 그래서 `parse` 는 **절대 던지지 않는다.**
 * 못 읽는 값은 조용히 기본값으로 떨어뜨린다. 잘린 링크 하나가 빈 화면이나 에러가 되면
 * "링크를 주고받는다" 는 이 설계의 목적 자체가 무너진다.
 *
 * `draft` / `query` 를 나눈 이유 — admin 은 **검색 버튼을 눌러야 조회되는** 관례(`SearchBox` 42곳)다.
 * 타이핑마다 조회하면 기존 화면들과 조작감이 갈린다.
 */
export const useListState = <TFilter>(config: ListStateConfig<TFilter>): ListState<TFilter> => {
  const {
    defaultFilter,
    parse,
    serialize,
    sortableFields,
    defaultSortBy,
    defaultSortOrder = 'DESC',
    defaultPageSize = DEFAULT_LIST_PAGE_SIZE,
  } = config;

  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * URL → 필터. `parse` 가 끝내 던지더라도 화면은 살아야 하므로 한 겹 더 감싼다.
   * (도메인 `parse` 가 실수로 던지게 바뀌는 날 이 화면만 조용히 기본값으로 뜬다.)
   */
  const appliedFilter = useMemo<TFilter>(() => {
    try {
      return parse(searchParams);
    } catch {
      return defaultFilter;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const page = readPositiveInt(searchParams.get('page'), 1);

  /**
   * 🔴 `pageSize` 도 **허용값으로 거른다** — 양의 정수라고 다 받으면 안 된다.
   *
   * `?pageSize=100000` 링크 하나가 전건 조회가 되고(서버 `PageDto` 에도 상한이 없다),
   * `?pageSize=7` 이면 페이저 드롭다운이 선택지에 없는 값을 표시한다.
   * `sortBy` 를 화이트리스트로 거르는 것과 같은 이유다 — "못 읽는 값은 기본값으로".
   */
  const rawPageSize = readPositiveInt(searchParams.get('pageSize'), defaultPageSize);
  const pageSize = (LIST_PAGE_SIZE_OPTIONS as readonly number[]).includes(rawPageSize) ? rawPageSize : defaultPageSize;

  const rawSortBy = searchParams.get('sortBy');
  const sortBy = rawSortBy && (!sortableFields || sortableFields.includes(rawSortBy)) ? rawSortBy : defaultSortBy;

  const rawSortOrder = searchParams.get('sortOrder')?.toUpperCase();
  const sortOrder: ListSortOrder = rawSortOrder === 'ASC' || rawSortOrder === 'DESC' ? rawSortOrder : defaultSortOrder;

  const [draft, setDraft] = useState<TFilter>(appliedFilter);

  /**
   * URL 이 화면 밖에서 바뀌면(뒤로/앞으로 가기 · 링크 붙여넣기) 검색 박스도 따라가야 한다.
   * 이게 없으면 뒤로 가기 후 **표는 이전 조건인데 검색 박스는 최신 조건**인 상태가 되고,
   * 그 상태로 검색을 누르면 사용자가 되돌아온 조건이 아니라 엉뚱한 조건으로 조회된다.
   *
   * 비교는 쿼리 문자열로 한다 — 필터 객체는 매 파싱마다 새 참조라 값 비교가 안 된다.
   */
  const lastSyncedSearch = useRef(searchParams.toString());

  useEffect(() => {
    const currentSearch = searchParams.toString();

    if (lastSyncedSearch.current === currentSearch) {
      return;
    }

    lastSyncedSearch.current = currentSearch;
    setDraft(appliedFilter);
  }, [searchParams, appliedFilter]);

  /**
   * URL 을 다시 쓰는 **유일한 통로**. 기본값 생략 규칙이 여기에만 있어야 한다 —
   * 두 벌이 되면 경로에 따라 서로 다른 주소가 만들어지고, 같은 조건인데 링크가 달라진다.
   *
   * `replace` 는 사용자가 밟지 않은 보정(`clampPage`)에서 쓴다.
   */
  const writeParams = useCallback(
    (
      params: { filter: TFilter; page: number; pageSize: number; sortBy?: string; sortOrder?: ListSortOrder },
      options?: { replace?: boolean },
    ) => {
      const next: Record<string, string> = { ...serialize(params.filter) };

      // 기본값은 싣지 않는다 — 공유되는 링크가 짧을수록 읽고 고치기 쉽다
      if (params.page !== 1) next.page = String(params.page);
      if (params.pageSize !== defaultPageSize) next.pageSize = String(params.pageSize);
      if (params.sortBy && params.sortBy !== defaultSortBy) next.sortBy = params.sortBy;
      if (params.sortOrder && params.sortOrder !== defaultSortOrder) next.sortOrder = params.sortOrder;

      setSearchParams(next, options);
    },
    [serialize, setSearchParams, defaultPageSize, defaultSortBy, defaultSortOrder],
  );

  const search = useCallback(() => {
    // 조건이 바뀌면 1페이지로 — 3페이지를 보다 조건을 좁히면 결과가 1페이지뿐일 수 있다
    writeParams({ filter: draft, page: 1, pageSize, sortBy, sortOrder });
  }, [draft, pageSize, sortBy, sortOrder, writeParams]);

  const reset = useCallback(() => {
    setDraft(defaultFilter);
    setSearchParams({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  const setPage = useCallback(
    (nextPage: number, nextPageSize?: number) => {
      writeParams({
        filter: appliedFilter,
        page: nextPage,
        pageSize: nextPageSize ?? pageSize,
        sortBy,
        sortOrder,
      });
    },
    [appliedFilter, pageSize, sortBy, sortOrder, writeParams],
  );

  const setSort = useCallback(
    (nextSortBy?: string, nextSortOrder?: ListSortOrder) => {
      // 정렬이 바뀌면 순서가 통째로 달라지므로 1페이지로 돌린다
      writeParams({
        filter: appliedFilter,
        page: 1,
        pageSize,
        sortBy: nextSortBy,
        sortOrder: nextSortOrder,
      });
    },
    [appliedFilter, pageSize, writeParams],
  );

  /**
   * 🔴 **범위를 넘은 페이지를 마지막 페이지로 되돌린다.**
   *
   * 클램프가 페이저 안에만 있으면 `?page=999` 로 들어온 링크나, 공유한 뒤 데이터가 줄어든
   * 링크에서 *"421건이 조회되었습니다"* 아래에 **빈 표**가 나온다. 링크 공유가 이 화면의
   * 출발점이라 실제로 밟기 쉬운 경로다.
   *
   * 총건수는 조회를 해봐야 알기에 화면이 응답을 받은 뒤 알려준다. 되돌릴 때는 `replace` 로
   * 바꿔 **뒤로 가기가 빈 페이지로 돌아가지 않게** 한다.
   *
   * 주소 조립은 `writeParams` 에 맡긴다 — 기본값 생략 규칙을 여기에 베껴 쓰면 두 경로가 어긋난다.
   */
  const clampPage = useCallback(
    (totalCount: number) => {
      // 0건은 조건이 좁은 것이지 페이지가 잘못된 게 아니다 — 1페이지에서 빈 표를 보여준다
      const lastPage = Math.max(Math.ceil(totalCount / pageSize), 1);

      if (page <= lastPage) {
        return;
      }

      writeParams({ filter: appliedFilter, page: lastPage, pageSize, sortBy, sortOrder }, { replace: true });
    },
    [appliedFilter, page, pageSize, sortBy, sortOrder, writeParams],
  );

  const query = useMemo<ListQuery<TFilter>>(
    () => ({ ...appliedFilter, page, pageSize, sortBy, sortOrder }),
    [appliedFilter, page, pageSize, sortBy, sortOrder],
  );

  return {
    draft,
    setDraft,
    query,
    page,
    pageSize,
    sortBy,
    sortOrder,
    search,
    reset,
    setPage,
    setSort,
    clampPage,
  };
};
